import { useState } from 'react';
import { Loader2, AlertTriangle, CheckCircle2, XCircle, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { diagnosticsAPI } from '../../lib/api';
import { cn } from '../../lib/utils';

const COMPONENT_LABELS = {
  cogs: 'COGS',
  invoice_financing: 'Financing',
  entity_opex: 'Entity Opex',
};

function ComponentClosureRow({ check }) {
  const residual = parseFloat(check.residual ?? '0');
  const isGreen = check.status === 'green';
  const totals = check.component_totals ?? {};

  return (
    <div className="flex items-start gap-2 text-sm">
      {isGreen
        ? <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
        : <XCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
      }
      <div className="min-w-0 w-full">
        <span className="font-medium">component closure</span>
        <div className="mt-1 text-xs font-mono space-y-0.5" data-testid="component-closure-rows">
          {(check.components ?? []).map((comp) => (
            <div key={comp} className="flex justify-between gap-4 text-muted-foreground" data-testid={`closure-row-${comp}`}>
              <span>{COMPONENT_LABELS[comp] ?? comp}</span>
              <span>{totals[comp] ?? '0.00'}</span>
            </div>
          ))}
          <div className={cn(
            'flex justify-between gap-4 font-semibold border-t pt-0.5 mt-0.5',
            residual === 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400',
          )} data-testid="closure-row-residual">
            <span>Residual</span>
            <span>{check.residual ?? '0.00'}</span>
          </div>
        </div>
        {check.mismatches?.length > 0 && (
          <div className="mt-1 text-xs text-red-500 font-mono">
            {check.mismatches.map((mm, i) => (
              <div key={i}>{mm.month}: costs={mm.total_costs} components={mm.component_sum} residual={mm.residual}</div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function CheckRow({ check }) {
  if (check.check === 'component_closure') {
    return <ComponentClosureRow check={check} />;
  }

  const passed = check.pass;
  return (
    <div className="flex items-start gap-2 text-sm">
      {passed
        ? <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
        : <XCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
      }
      <div className="min-w-0">
        <span className="font-medium">{check.check.replace(/_/g, ' ')}</span>
        {check.mismatches?.length > 0 && (
          <div className="mt-1 text-xs text-muted-foreground font-mono">
            {check.mismatches.map((mm, i) => (
              <div key={i}>{mm.month}: net={mm.net_result} expected={mm.expected_net}</div>
            ))}
          </div>
        )}
        {check.error && (
          <div className="mt-1 text-xs text-red-500">{check.error}</div>
        )}
      </div>
    </div>
  );
}

export function PnlReconciliationPanel() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const run = () => {
    setLoading(true);
    setError(null);
    diagnosticsAPI.pnlReconciliation()
      .then(({ data: d }) => setData(d))
      .catch((err) => setError(err.message || 'Failed'))
      .finally(() => setLoading(false));
  };

  const verdict = data?.verdict;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          P&L Reconciliation
          {verdict && (
            verdict === 'pass'
              ? <Badge variant="default" className="ml-2 bg-green-600">PASS</Badge>
              : verdict === 'error'
                ? <Badge variant="outline" className="ml-2 border-amber-500 text-amber-600">ERROR</Badge>
                : <Badge variant="destructive" className="ml-2">FAIL</Badge>
          )}
          <Button variant="outline" size="sm" className="ml-auto" onClick={run} disabled={loading}>
            {loading
              ? <Loader2 className="h-4 w-4 animate-spin" />
              : <RefreshCw className="h-4 w-4" />
            }
            <span className="ml-1.5">{data ? 'Re-run' : 'Run'}</span>
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!data && !loading && !error && (
          <p className="text-sm text-muted-foreground">
            Verifies entity_pnl service arithmetic, source registry alignment, and personal sum-of-parts.
          </p>
        )}
        {loading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Running reconciliation checks across all entities...
          </div>
        )}
        {error && (
          <div className="flex items-center gap-2 text-sm text-red-500">
            <AlertTriangle className="h-4 w-4" />
            {error}
          </div>
        )}
        {data && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Year {data.year}, {data.entity_count} entities checked
            </p>

            {/* Entity results */}
            <div className="space-y-3">
              {data.entities.map((ent) => (
                <div key={ent.entity_id} className="border rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-2">
                    {ent.verdict === 'pass'
                      ? <CheckCircle2 className="h-4 w-4 text-green-500" />
                      : ent.verdict === 'error'
                        ? <AlertTriangle className="h-4 w-4 text-amber-500" />
                        : <XCircle className="h-4 w-4 text-red-500" />
                    }
                    <span className="font-medium text-sm">{ent.entity_name}</span>
                    <Badge variant="outline" className="text-xs">{ent.source}</Badge>
                  </div>
                  {ent.error && (
                    <p className="text-xs text-red-500 ml-6">{ent.error}</p>
                  )}
                  {ent.checks && (
                    <div className="ml-6 space-y-1.5">
                      {ent.checks.map((c, i) => <CheckRow key={i} check={c} />)}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Personal scope */}
            {data.personal_scope && (
              <div className="border rounded-lg p-3">
                <div className="flex items-center gap-2 mb-2">
                  {data.personal_scope.pass
                    ? <CheckCircle2 className="h-4 w-4 text-green-500" />
                    : <XCircle className="h-4 w-4 text-red-500" />
                  }
                  <span className="font-medium text-sm">Personal Scope (sum-of-parts)</span>
                </div>
                {data.personal_scope.error && (
                  <p className="text-xs text-red-500 ml-6">{data.personal_scope.error}</p>
                )}
                {data.personal_scope.personal_total && (
                  <div className="ml-6 text-xs text-muted-foreground font-mono space-y-0.5">
                    <div>Personal total: rev={data.personal_scope.personal_total.revenue} costs={data.personal_scope.personal_total.costs}</div>
                    <div>Constituent sum: rev={data.personal_scope.constituent_sum.revenue} costs={data.personal_scope.constituent_sum.costs}</div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
